import React, { useEffect, useState } from 'react';
import './FeatureStyles.css';
import './AIBugSolvingFeature.css';

// Three solution paths branching from the bug node
const solutionPaths = [
  { id: 1, endY: 50, curveY: 50 },   // Top path
  { id: 2, endY: 100, curveY: 100 }, // Middle path (winning)
  { id: 3, endY: 150, curveY: 150 }, // Bottom path
];

const AIBugSolvingAnimation = () => {
  const [phase, setPhase] = useState(0);
  const [scanIndex, setScanIndex] = useState(0);
  // 0: bug identified (paths shown), 1: scanning paths, 2: solution found

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(prev => (prev + 1) % 3);
      setScanIndex(0);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  // Sequential scanning during phase 1
  useEffect(() => {
    if (phase === 1) {
      const scanInterval = setInterval(() => {
        setScanIndex(prev => (prev + 1) % 4); // 0,1,2,3 -> scan each path then done
      }, 800);
      return () => clearInterval(scanInterval);
    }
  }, [phase]);

  const bugX = 50;
  const bugY = 100;
  const targetX = 270;

  return (
    <div className="feature-animation abstract ai-bug-solving-abstract">
      <svg className="abstract-canvas" viewBox="0 0 320 200">
        <defs>
          <linearGradient id="solving-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>

        {/* Bug source node */}
        <g className="bug-node-group">
          <circle
            className={`abstract-node pink ${phase === 0 ? 'active' : ''}`}
            cx={bugX}
            cy={bugY}
            r="24"
          />
          {/* Exclamation mark */}
          <text className="node-icon" x={bugX} y={bugY - 3} style={{ fontSize: '16px' }}>!</text>
        </g>

        {/* Solution paths */}
        {solutionPaths.map((path, i) => {
          const isSelected = i === 1; // Middle path wins
          const isFaded = phase === 2 && !isSelected;
          const isScanning = phase === 1 && scanIndex === i;
          const isHighlighted = phase === 2 && isSelected;

          return (
            <g key={path.id} className="solution-path-group">
              {/* Path line */}
              <path
                className={`abstract-line solution-line ${
                  isFaded ? 'faded' : ''
                } ${isHighlighted ? 'gradient highlighted' : 'dashed'} ${
                  isScanning ? 'scanning' : ''
                }`}
                d={`M ${bugX + 24} ${bugY} Q ${160} ${path.curveY} ${targetX - 16} ${path.endY}`}
                style={{
                  stroke: isHighlighted ? 'url(#solving-gradient)' : undefined,
                  strokeWidth: isHighlighted ? 3 : 2
                }}
              />

              {/* Target node */}
              <circle
                className={`abstract-node ${
                  isFaded ? 'gray dimmed' : isHighlighted ? 'blue active' : 'gray'
                } ${isScanning ? 'scanning-node' : ''}`}
                cx={targetX}
                cy={path.endY}
                r="16"
              />

              {/* Checkmark on selected node */}
              {isHighlighted && (
                <path
                  className="solution-check"
                  d={`M ${targetX - 6} ${path.endY} L ${targetX - 2} ${path.endY + 4} L ${targetX + 6} ${path.endY - 4}`}
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Scanning pulse traveling along path */}
              {isScanning && (
                <circle
                  className="traveling-particle pink"
                  r="5"
                  cx={bugX + 24}
                  cy={bugY}
                >
                  <animateMotion
                    dur="0.6s"
                    repeatCount="1"
                    fill="freeze"
                    path={`M 0 0 Q ${160 - bugX - 24} ${path.curveY - bugY} ${targetX - 16 - bugX - 24} ${path.endY - bugY}`}
                  />
                </circle>
              )}
            </g>
          );
        })}

      </svg>

      {/* Result label */}
      {phase === 2 && <span className="result-label">Solved</span>}
    </div>
  );
};

const AIBugSolvingFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  title: "AI Bug Solving Suggestions",
  description: "Go beyond detection. AI analyzes your codebase, test failures, and historical fixes to suggest actual solutions. Get code-level recommendations, related test cases, and links to similar past fixes. Optionally send suggestions straight to GitHub or GitLab as issues for an AI coding agent to resolve automatically.",
  highlight: "Paid Plan",
  animation: <AIBugSolvingAnimation />
};

export default AIBugSolvingFeature;
